import { useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Heart, AlertCircle, Phone, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

const CHIPS = [
  { id: "nausea", label: "Nausea" },
  { id: "fatigue", label: "Fatigue" },
  { id: "gi_upset", label: "GI upset" },
  { id: "headache", label: "Headache" },
  { id: "dizziness", label: "Dizziness" },
  { id: "injection_site_reaction", label: "Injection site reaction" },
  { id: "decreased_appetite", label: "Decreased appetite" },
  { id: "constipation", label: "Constipation" },
  { id: "diarrhea", label: "Diarrhea" },
  { id: "other", label: "Other" },
];

const SEVERITIES = [
  { id: "mild", label: "Mild", emoji: "🟢" },
  { id: "moderate", label: "Moderate", emoji: "🟡" },
  { id: "severe", label: "Severe", emoji: "🔴" },
];

/**
 * Symptom logger that's always available to ANY logged-in patient — no
 * subscription required. Wraps the same `/wellness/side-effect` endpoint
 * the Companion uses, so a patient who's between subscriptions can still
 * report side-effects to the clinical team.
 *
 * Lives on the patient dashboard. Submission triggers the same severity-
 * escalation modal (chat-with-staff or customer-service phone) for severe
 * reports as the Companion path.
 */
export default function PatientSymptomCard() {
  const [showDialog, setShowDialog] = useState(false);
  const [show911Guard, setShow911Guard] = useState(false);
  const [chips, setChips] = useState([]);
  const [severity, setSeverity] = useState("mild");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [escalation, setEscalation] = useState(null);

  const openLogger = () => {
    // Show the 911-first safety modal every time the patient starts a
    // fresh symptom log — matches the mobile `guardWith911Check` flow.
    setChips([]); setNotes(""); setSeverity("mild");
    setShow911Guard(true);
  };
  const proceedFrom911 = () => {
    setShow911Guard(false);
    setShowDialog(true);
  };

  const toggleChip = (id) =>
    setChips((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));

  const submit = async () => {
    if (chips.length === 0) {
      toast.error("Select at least one symptom");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await API.post("/wellness/side-effect", {
        chips,
        severity,
        notes: notes.trim() || undefined,
      });
      setShowDialog(false);
      setChips([]); setNotes(""); setSeverity("mild");
      if (data.needs_escalation) {
        setEscalation({ needs_escalation: true, chat_available: data.chat_available });
      } else {
        toast.success("Symptom logged. Your prescriber will see this in your chart.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="bg-rose-50 dark:bg-rose-950/40 rounded-2xl border-2 border-rose-300 dark:border-rose-700/60 p-5 flex items-center gap-4 shadow-[0_4px_18px_-6px_rgba(244,63,94,0.35)]"
        data-testid="patient-symptom-card"
      >
        <div className="w-12 h-12 flex-shrink-0 rounded-xl bg-rose-600 flex items-center justify-center shadow-md shadow-rose-300">
          <Heart className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider font-bold text-rose-700 dark:text-rose-300">Symptom check-in</p>
          <h3
            className="text-lg font-bold leading-tight text-slate-900 dark:text-white mt-0.5"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Not feeling great? Log it.
          </h3>
          <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
            Tell your care team what&apos;s going on — available even between subscriptions.
          </p>
        </div>
        <Button
          onClick={openLogger}
          className="flex-shrink-0 bg-rose-600 hover:bg-rose-700 text-white rounded-full text-sm"
          data-testid="patient-symptom-card-open"
        >
          Log
        </Button>
      </div>

      {/* 911-first safety modal — shown BEFORE the log dialog every time. */}
      <Dialog open={show911Guard} onOpenChange={setShow911Guard}>
        <DialogContent className="max-w-md" data-testid="symptom-911-guard">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <AlertCircle className="w-5 h-5" /> Is this a medical emergency?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-2xl border-2 border-rose-300 bg-rose-50 p-4 text-sm text-rose-900">
              <p className="font-bold text-base leading-tight">If this is a medical emergency, call 911 immediately.</p>
              <p className="mt-2 text-xs leading-relaxed">
                GLPFlo is a telehealth platform for chronic weight management — we can&apos;t provide emergency care. Symptoms like chest pain, trouble breathing, severe allergic reaction, or fainting need emergency services right now.
              </p>
            </div>
            <a
              href="tel:911"
              className="flex items-center justify-center gap-2 rounded-full bg-rose-600 hover:bg-rose-700 text-white font-bold py-3 text-sm"
              data-testid="symptom-911-call"
            >
              <Phone className="w-4 h-4" /> Call 911
            </a>
            <Button
              onClick={proceedFrom911}
              variant="outline"
              className="w-full rounded-full text-sm"
              data-testid="symptom-911-proceed"
            >
              Proceed with logging symptoms
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Symptom dialog — mirrors the Companion side-effect flow exactly. */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="patient-symptom-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <Heart className="w-5 h-5 text-rose-600" /> How are you feeling?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">What&apos;s going on? (pick any that apply)</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {CHIPS.map((c) => {
                  const active = chips.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleChip(c.id)}
                      className={`text-xs rounded-lg px-3 py-2 border text-left transition-colors ${active
                        ? "bg-rose-100 border-rose-300 text-rose-800 font-bold"
                        : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-rose-200"}`}
                      data-testid={`patient-symptom-chip-${c.id}`}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">How severe?</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {SEVERITIES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSeverity(s.id)}
                    className={`text-xs rounded-lg px-3 py-2 border transition-colors ${severity === s.id
                      ? "bg-rose-600 text-white border-rose-600 font-bold"
                      : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"}`}
                    data-testid={`patient-symptom-severity-${s.id}`}
                  >
                    {s.emoji} {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm">Notes (optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. started 4 hours after my injection"
                maxLength={1000}
                className="mt-1 rounded-xl"
                data-testid="patient-symptom-notes"
              />
            </div>
            <p className="text-xs text-slate-500">
              Your prescriber can see this log in your chart at your next visit.{" "}
              <strong>If severe, we&apos;ll connect you with help right away.</strong>
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={submitting || chips.length === 0}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="patient-symptom-save"
            >
              {submitting ? "Logging..." : "Log symptom"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Severity escalation — same flow as Companion. */}
      <Dialog open={!!escalation} onOpenChange={() => setEscalation(null)}>
        <DialogContent className="max-w-md" data-testid="patient-symptom-escalation">
          <DialogHeader>
            <DialogTitle className="text-rose-700 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <AlertCircle className="w-5 h-5" /> Severe symptoms — let&apos;s get you help
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
            {escalation?.chat_available ? (
              <>
                <p>Your symptoms have been logged. Our clinical team is online — let&apos;s start a chat right now.</p>
                <Link to="/patient/chat" onClick={() => setEscalation(null)}>
                  <Button className="w-full bg-rose-600 hover:bg-rose-700 text-white">
                    <MessageCircle className="w-4 h-4 mr-2" /> Open chat with my care team
                  </Button>
                </Link>
                <p className="text-xs text-slate-500"><strong>For any medical emergency, call 911.</strong></p>
              </>
            ) : (
              <>
                <p>Your symptoms have been logged. Our chat team isn&apos;t online right now — for severe symptoms, please call us directly:</p>
                <a
                  href="tel:+17275162142"
                  className="block text-center text-lg font-bold text-rose-700 bg-rose-50 rounded-xl py-3 hover:bg-rose-100"
                  data-testid="patient-symptom-call"
                >
                  <Phone className="w-5 h-5 inline mr-2" /> (727) 516-2142
                </a>
                <p className="text-xs text-slate-500">After hours, leave a voicemail and a clinician will call you back. <strong>For any medical emergency, call 911.</strong></p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEscalation(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
